import React, {Component} from "react"
import {countBy} from "lodash"

import {Hr} from "@irati/ui/src/Layout"
import {H4, Body} from "@irati/ui/src/Typography"
import FormattedMessage from "@irati/utils/lang"
import Spacing from "@irati/ui/src/Spacing"
import Flex from "@irati/ui/src/Flex"
import History from "@irati/utils/history"
import {RouterLink} from "@irati/ui/src/Link"
import {OnboardingReportStep} from "@irati/types"
import Button from "@irati/ui/src/Button"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import {adminDashboardTracking} from "@irati-tracking/admin"

import {
  ADD_PEOPLE_SINGLE,
  SETTINGS_CUSTOMISE_BRAND,
  SETTINGS_CUSTOMISE_WELCOME_EMAIL,
  SETTINGS_LOGIN_INTEGRATION,
  ADD_PEOPLE_MULTIPLE,
  ADD_TEAM,
  VIEW_TEAM,
  SETTINGS_CUSTOMISE_BENEFITS,
  SETTINGS_CUSTOMISE_VALUES,
  SETTINGS_CUSTOMISE_CELEBRATIONS} from "../../../routes/routeConfig"
import ProgressBar from "../ProgressBar"
import ProgressMetric, {Props as ProgressMetricProps} from "./ProgressMetric"

interface Props {
  metrics: [OnboardingReportStep]
}

interface State {
  active: string|null,
  nextStep: ProgressMetricProps|null,
  completedSteps: number,
  isCollapsed: boolean
}

const ACTIVATE_ADMIN = "ACTIVATE_ADMIN"
const CUSTOMISE_LOGO_COLOUR = "CUSTOMISE_LOGO_OR_COLOUR"
const CUSTOMISE_WELCOME_EMAIL = "CUSTOMISE_WELCOME_MESSAGE"
const INTEGRATIONS = "SSO_ENABLED"
const INVITE_PEOPLE = "INVITE_PEOPLE"
const CREATE_TEAMS = "CREATE_TEAM"
const ASSIGN_MANAGERS = "ASSIGN_TEAM_MANAGER"
const CUSTOM_BENEFITS = "CREATE_COMPANY_BENEFIT"
const COMPANY_VALUES = "CREATE_COMPANY_VALUE"
const CELEBRATIONS = "CREATE_COMPANY_CELEBRATION"
const PEOPLE_DIRECTORY = "DIRECTORY_SYNC_ENABLED"
const CULTURE_HUB = "CREATE_CULTURE_HUB_TILE"

class ProgressMetrics extends Component<Props, State> {
  state: State = {
    active: null,
    nextStep: null,
    completedSteps: 0,
    isCollapsed: false
  }

  componentDidMount() {
    const progressMetrics = this.buildMetrics()
    const nextStep = progressMetrics.find((progressMetric) => !progressMetric.isCompleted)
    const completedSteps = countBy(progressMetrics, (metric) => metric.isCompleted).true
    this.setState({completedSteps})
    if (nextStep) {
      this.setState({nextStep})
      nextStep.onClickChevron()
    }
  }

  handleChevronClick = (active: string) => {
    if (active === this.state.active) {
      return this.setState({active: null})
    }
    this.setState({active})
  }

  isStepComplete = (step: string) => {
    const {metrics} = this.props
    return metrics.find((metric) => metric.step === step)?.complete
  }

  buildMetrics = () => {
    const {active} = this.state
    return [
      {
        isCompleted: true,
        isOpen: active === ACTIVATE_ADMIN,
        onClickChevron: () => this.handleChevronClick(ACTIVATE_ADMIN),
        title: "admin.components.progressMetrics.activateAdmin.title",
        text: "admin.components.progressMetrics.activateAdmin.text",
        buttonText: "admin.components.progressMetrics.activateAdmin.buttonText",
        page: ADD_PEOPLE_SINGLE,
        id: "adminSection__dashboard_activateAdminAccountDropdown",
        tracking: adminDashboardTracking.clickInviteAdmin
      },
      {
        isCompleted: this.isStepComplete(CUSTOMISE_LOGO_COLOUR),
        isOpen: active === CUSTOMISE_LOGO_COLOUR,
        onClickChevron: () => this.handleChevronClick(CUSTOMISE_LOGO_COLOUR),
        title: "admin.components.progressMetrics.customiseLogoColour.title",
        text: "admin.components.progressMetrics.customiseLogoColour.text",
        page: SETTINGS_CUSTOMISE_BRAND,
        id: "adminSection__dashboard_customiseLogoAndColourDropdown",
        tracking: adminDashboardTracking.clickCustomBrand
      },
      {
        isCompleted: this.isStepComplete(CUSTOMISE_WELCOME_EMAIL),
        isOpen: active === CUSTOMISE_WELCOME_EMAIL,
        onClickChevron: () => this.handleChevronClick(CUSTOMISE_WELCOME_EMAIL),
        title: "admin.components.progressMetrics.customiseWelcomeEmail.title",
        text: "admin.components.progressMetrics.customiseWelcomeEmail.text",
        page: SETTINGS_CUSTOMISE_WELCOME_EMAIL,
        id: "adminSection__dashboard_customiseWelcomeEmailDropdown",
        tracking: adminDashboardTracking.clickCustomEmail
      },
      {
        isCompleted: this.isStepComplete(INTEGRATIONS),
        isOpen: active === INTEGRATIONS,
        onClickChevron: () => this.handleChevronClick(INTEGRATIONS),
        title: "admin.components.progressMetrics.singleSignOn.title",
        text: "admin.components.progressMetrics.singleSignOn.text",
        page: SETTINGS_LOGIN_INTEGRATION,
        id: "adminSection__dashboard_enableSingleSignOnDropdown",
        tracking: adminDashboardTracking.clickEnableSSO
      },
      {
        isCompleted: this.isStepComplete(CREATE_TEAMS),
        isOpen: active === CREATE_TEAMS,
        onClickChevron: () => this.handleChevronClick(CREATE_TEAMS),
        title: "admin.components.progressMetrics.createTeams.title",
        text: "admin.components.progressMetrics.createTeams.text",
        page: ADD_TEAM,
        id: "adminSection__dashboard_createTeamsDropdown",
        tracking: adminDashboardTracking.clickCreateTeams
      },
      {
        isCompleted: this.isStepComplete(INVITE_PEOPLE),
        isOpen: active === INVITE_PEOPLE,
        onClickChevron: () => this.handleChevronClick(INVITE_PEOPLE),
        title: "admin.components.progressMetrics.invitePeople.title",
        text: "admin.components.progressMetrics.invitePeople.text",
        buttonText: "admin.components.progressMetrics.invitePeople.buttonText",
        secondaryButtonText: "admin.components.progressMetrics.invitePeople.secondaryButtonText",
        page: ADD_PEOPLE_MULTIPLE,
        secondPage: `${SETTINGS_LOGIN_INTEGRATION}?dsync`,
        id: "adminSection__dashboard_invitePeopleDropdown",
        tracking: adminDashboardTracking.clickInvitePeopleUpload,
        secondaryTracking: adminDashboardTracking.clickInvitePeopleDSYNC
      },
      {
        isCompleted: this.isStepComplete(ASSIGN_MANAGERS),
        isOpen: active === ASSIGN_MANAGERS,
        onClickChevron: () => this.handleChevronClick(ASSIGN_MANAGERS),
        title: "admin.components.progressMetrics.assignManagers.title",
        text: "admin.components.progressMetrics.assignManagers.text",
        page: VIEW_TEAM,
        id: "adminSection__dashboard_assignTeamManagersDropdown",
        tracking: adminDashboardTracking.clickAssignManagers
      },
      {
        isCompleted: this.isStepComplete(CUSTOM_BENEFITS),
        isOpen: active === CUSTOM_BENEFITS,
        onClickChevron: () => this.handleChevronClick(CUSTOM_BENEFITS),
        title: "admin.components.progressMetrics.customBenefits.title",
        text: "admin.components.progressMetrics.customBenefits.text",
        page: SETTINGS_CUSTOMISE_BENEFITS,
        id: "adminSection__dashboard_addCustomBenefitsDropdown",
        tracking: adminDashboardTracking.clickAddCustomBenefits
      },
      {
        isCompleted: this.isStepComplete(COMPANY_VALUES),
        isOpen: active === COMPANY_VALUES,
        onClickChevron: () => this.handleChevronClick(COMPANY_VALUES),
        title: "admin.components.progressMetrics.companyValues.title",
        text: "admin.components.progressMetrics.companyValues.text",
        page: SETTINGS_CUSTOMISE_VALUES,
        id: "adminSection__dashboard_addCompanyValuesDropdown",
        tracking: adminDashboardTracking.clickAddCompanyValues
      },
      {
        isCompleted: this.isStepComplete(CELEBRATIONS),
        isOpen: active === CELEBRATIONS,
        onClickChevron: () => this.handleChevronClick(CELEBRATIONS),
        title: "admin.components.progressMetrics.companyCelebrations.title",
        text: "admin.components.progressMetrics.companyCelebrations.text",
        page: SETTINGS_CUSTOMISE_CELEBRATIONS,
        id: "adminSection__dashboard_addCompanyCelebrationsDropdown",
        tracking: adminDashboardTracking.clickAddCompanyCelebrations
      },
      {
        isCompleted: this.isStepComplete(PEOPLE_DIRECTORY),
        isOpen: active === PEOPLE_DIRECTORY,
        onClickChevron: () => this.handleChevronClick(PEOPLE_DIRECTORY),
        title: "admin.components.progressMetrics.peopleDirectory.title",
        text: "admin.components.progressMetrics.peopleDirectory.text",
        page: SETTINGS_LOGIN_INTEGRATION,
        id: "adminSection__dashboard_linkYourPeopleDirectoryDropdown",
        tracking: adminDashboardTracking.clickLinkPeopleDirectory
      },
      {
        isCompleted: this.isStepComplete(CULTURE_HUB),
        isOpen: active === CULTURE_HUB,
        onClickChevron: () => this.handleChevronClick(CULTURE_HUB),
        title: "admin.components.progressMetrics.cultureHub.title",
        text: "admin.components.progressMetrics.invitePeople.cultureHub.text",
        onClick: () => History.push("/home/edit"),
        id: "adminSection__dashboard_editCultureHubDropdown",
        tracking: adminDashboardTracking.clickEditCultureHub
      }
    ]
  }

  handleExpanderClick = () => {
    this.setState((prevState: State) => ({isCollapsed: !prevState.isCollapsed}))
  }

  render() {
    const {nextStep, completedSteps, isCollapsed} = this.state
    const progressMetrics = this.buildMetrics()
    const {page, title} = nextStep || {}
    const expandButtonText = isCollapsed
      ? "admin.components.progressMetrics.button.expand"
      : "admin.components.progressMetrics.button.collapse"

    return (<>
      <Spacing padding={[2, 2, 0]}>
        <Flex justifyContent="flex-end">
          <Button tertiary onClick={this.handleExpanderClick} marginBottom={3}>
            <FormattedMessage id={expandButtonText} />
            <Icon marginLeft={1} icon={isCollapsed ? IconTypes.chevronDown : IconTypes.chevronUp} />
          </Button>
        </Flex>
        <H4 marginBottom={2}>
          <FormattedMessage id="admin.components.progressMetrics.title" />
        </H4>
        <ProgressBar completedSteps={completedSteps} />
        {nextStep &&
          <Flex alignItems="center">
            <Body marginRight={1}>
              <FormattedMessage id="admin.components.progressMetrics.next" />
            </Body>
            <RouterLink cta to={page}>
              <FormattedMessage id={title} />
            </RouterLink>
          </Flex>
        }
        {!isCollapsed && <Hr />}
      </Spacing>
      {!isCollapsed && progressMetrics.map((progressMetric, index) =>
        <ProgressMetric
          {...progressMetric}
          index={index + 1} />
      )}
    </>)
  }
}

export default ProgressMetrics
